import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Play, Loader2, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { cpFetch } from '@/lib/cpClient';

interface AgentExecutionModalProps {
  agent: any;
  isOpen: boolean;
  onClose: () => void;
}

export default function AgentExecutionModal({ agent, isOpen, onClose }: AgentExecutionModalProps) {
  const [input, setInput] = useState('');
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const handleExecute = async () => {
    setExecuting(true);
    setError(null);
    setResult(null);

    try {
      let parsedInput: any;
      try {
        parsedInput = input.trim() ? JSON.parse(input) : {};
      } catch {
        // Plain-text convenience mode.
        // Some agents (notably contextualizer-pipeline) expect a specific shape, not { input: "..." }.
        const caps: string[] = Array.isArray(agent?.capabilities) ? agent.capabilities : [];
        if (caps.includes('ensure_templates_for_document')) {
          parsedInput = {
            filename: 'manual-text',
            extracted_text: input,
            iku_category: 'iku-products',
          };
        } else {
          parsedInput = { input: input };
        }
      }

      const response = await cpFetch(`/api/volcano/execute/${agent.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          input: parsedInput,
          context: {
            tenant_id: 'dev',
            user_id: 'dev@devcockpit.ai',
          },
        }),
      });

      if (response.success) {
        setResult(response);
      } else {
        setError(response.error || 'Execution failed');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to execute agent');
    } finally {
      setExecuting(false);
    }
  };

  const handleClose = () => {
    setInput('');
    setResult(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Play className="w-5 h-5" />
            Execute Agent: {agent.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Agent Info */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Badge>{agent.deployment_stage}</Badge>
              <Badge variant="outline">{agent.domain}</Badge>
            </div>
            <p className="text-sm text-gray-600">{agent.description}</p>
          </div>

          {/* Input Section */}
          {!result && !error && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Input (JSON or text)</label>
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder='{"query": "your input here"} or just plain text'
                rows={6}
                className="font-mono text-sm"
              />
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  Enter JSON object or plain text. Leave empty for no input.
                </p>
                <Button
                  onClick={handleExecute}
                  disabled={executing}
                  className="gap-2"
                >
                  {executing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Executing...
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4" />
                      Execute
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Executing State */}
          {executing && (
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
              <div className="text-center">
                <p className="font-medium">Executing agent workflow...</p>
                <p className="text-sm text-gray-500">This may take a few moments</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-medium text-red-900">Execution Failed</h4>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <Button variant="outline" onClick={() => { setError(null); setResult(null); }}>
                  Try Again
                </Button>
                <Button variant="ghost" onClick={handleClose}>
                  Close
                </Button>
              </div>
            </div>
          )}

          {/* Success State */}
          {result && !error && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <div>
                    <h4 className="font-medium text-green-900">Execution Successful</h4>
                    <p className="text-sm text-green-700">
                      Completed in {result.duration_ms}ms
                    </p>
                  </div>
                </div>
              </div>

              {/* Steps */}
              {result.result && Array.isArray(result.result) && (
                <div className="space-y-2">
                  <h4 className="font-medium text-sm">Execution Steps</h4>
                  <div className="space-y-2">
                    {result.result.map((step: any, idx: number) => (
                      <div key={idx} className="bg-gray-50 rounded-lg p-3 border">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-sm">Step {idx + 1}</span>
                          <Badge variant={step.status === 'completed' ? 'default' : 'destructive'}>
                            {step.status}
                          </Badge>
                        </div>
                        {step.llmOutput && (
                          <pre className="text-xs bg-white p-2 rounded overflow-x-auto max-h-40">
                            {typeof step.llmOutput === 'string' 
                              ? step.llmOutput 
                              : JSON.stringify(step.llmOutput, null, 2)}
                          </pre>
                        )}
                        {step.usage && (
                          <div className="mt-2 text-xs text-gray-600">
                            Tokens: {step.usage.total_tokens} • 
                            Cost: ${((step.usage.total_tokens / 1000) * 0.003).toFixed(4)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                <Button onClick={() => { setResult(null); setError(null); }}>
                  Execute Again
                </Button>
                <Button variant="outline" onClick={handleClose}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

